/**
 * Card de configuração do "Não Perturbe" (DND).
 *
 * Permite definir UMA janela diária (início/fim + dias da semana + timezone)
 * e marcar quais canais externos ficam silenciados durante essa janela.
 * O canal in-app NUNCA é silenciado (notificação sempre vai ao centro).
 *
 * @version 1.1.0 - 2026-06-05 — Substituído Switch por IconToggle (padrão Departamentos)
 * @version 1.0.0 - 2026-06-05 — F3 concluída
 */
import { useEffect, useState } from 'react';
import { MoonStar, Smartphone, Mail, MessageCircle } from 'lucide-react';
import { IconToggle } from '@/components/ui/icon-toggle';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { DndSchedule, NotificationChannel } from '@/hooks/useNotificationSettings';

/** Lista de timezones mais usadas no Brasil + UTC. */
const TIMEZONES = [
  'America/Sao_Paulo',
  'America/Manaus',
  'America/Cuiaba',
  'America/Belem',
  'America/Fortaleza',
  'America/Recife',
  'America/Noronha',
  'UTC',
];

/** Rótulo dos 7 dias (ISO: 1=Seg ... 7=Dom). */
const DAYS = [
  { iso: 1, label: 'Seg' },
  { iso: 2, label: 'Ter' },
  { iso: 3, label: 'Qua' },
  { iso: 4, label: 'Qui' },
  { iso: 5, label: 'Sex' },
  { iso: 6, label: 'Sáb' },
  { iso: 7, label: 'Dom' },
];

/** Ícone por canal silenciável. */
const CHANNEL_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  push_fcm: Smartphone, email: Mail, sms: MessageCircle, whatsapp: MessageCircle,
};

const IMPLEMENTED = new Set(['push_fcm']);

interface Props {
  value: DndSchedule | null;
  channels: NotificationChannel[];
  onSave: (next: DndSchedule) => void;
  saving?: boolean;
}

/** Default razoável quando o usuário nunca configurou DND. */
const DEFAULT: DndSchedule = {
  is_enabled: false,
  start_time: '22:00',
  end_time:   '07:00',
  days_of_week: [1, 2, 3, 4, 5, 6, 7],
  timezone: 'America/Sao_Paulo',
  channels_silenced: ['push_fcm', 'email', 'sms', 'whatsapp'],
};

export function NotificationDndCard({ value, channels, onSave, saving }: Props) {
  const [local, setLocal] = useState<DndSchedule>(value ?? DEFAULT);

  // Sincroniza quando o snapshot inicial chega
  useEffect(() => { if (value) setLocal(value); }, [value]);

  const externals = channels.filter(c => c.is_external);

  const toggleDay = (iso: number) => {
    setLocal(prev => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(iso)
        ? prev.days_of_week.filter(d => d !== iso)
        : [...prev.days_of_week, iso].sort(),
    }));
  };

  const toggleChannel = (code: string) => {
    setLocal(prev => ({
      ...prev,
      channels_silenced: prev.channels_silenced.includes(code)
        ? prev.channels_silenced.filter(c => c !== code)
        : [...prev.channels_silenced, code],
    }));
  };

  return (
    <div className="rounded-xl border border-border/60 bg-card p-5 space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
            <MoonStar className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h3 className="text-sm font-semibold tracking-tight">Não Perturbe</h3>
            <p className="text-xs text-muted-foreground">
              Define uma janela diária em que canais externos ficam silenciados.
              O Centro de Notificações continua recebendo tudo.
            </p>
          </div>
        </div>
        <IconToggle
          checked={local.is_enabled}
          onCheckedChange={(v) => setLocal(prev => ({ ...prev, is_enabled: v }))}
          aria-label="Ativar Não Perturbe"
          tooltipOn="Desativar Não Perturbe"
          tooltipOff="Ativar Não Perturbe"
        />
      </div>

      {/* Horários + timezone */}
      <div className={cn('grid grid-cols-1 sm:grid-cols-3 gap-3', !local.is_enabled && 'opacity-60 pointer-events-none')}>
        <div className="space-y-1.5">
          <Label className="text-xs">Início</Label>
          <Input
            type="time"
            value={local.start_time}
            onChange={(e) => setLocal(prev => ({ ...prev, start_time: e.target.value }))}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Fim</Label>
          <Input
            type="time"
            value={local.end_time}
            onChange={(e) => setLocal(prev => ({ ...prev, end_time: e.target.value }))}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Fuso horário</Label>
          <Select
            value={local.timezone}
            onValueChange={(v) => setLocal(prev => ({ ...prev, timezone: v }))}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {TIMEZONES.map(tz => (
                <SelectItem key={tz} value={tz}>{tz}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Dias da semana */}
      <div className={cn('space-y-2', !local.is_enabled && 'opacity-60 pointer-events-none')}>
        <Label className="text-xs">Dias da semana</Label>
        <div className="flex flex-wrap gap-1.5">
          {DAYS.map(d => {
            const active = local.days_of_week.includes(d.iso);
            return (
              <button
                key={d.iso}
                type="button"
                onClick={() => toggleDay(d.iso)}
                className={cn(
                  'h-8 px-3 rounded-md text-xs font-medium transition-colors border',
                  active
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-muted-foreground border-border hover:bg-muted',
                )}
              >
                {d.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Canais silenciados */}
      <div className={cn('space-y-2', !local.is_enabled && 'opacity-60 pointer-events-none')}>
        <Label className="text-xs">Canais silenciados durante a janela</Label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {externals.map(c => {
            const Icon = CHANNEL_ICON[c.code] ?? Smartphone;
            const checked = local.channels_silenced.includes(c.code);
            const impl = IMPLEMENTED.has(c.code);
            return (
              <button
                key={c.code}
                type="button"
                onClick={() => impl && toggleChannel(c.code)}
                disabled={!impl}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-md border text-xs transition-colors',
                  checked && impl
                    ? 'bg-primary/10 border-primary text-foreground'
                    : 'bg-background border-border text-muted-foreground',
                  !impl && 'opacity-60 cursor-not-allowed',
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                <span className="truncate flex-1 text-left">{c.name}</span>
                {!impl && (
                  <Badge variant="outline" className="h-4 px-1 text-[9px] font-normal">
                    Em breve
                  </Badge>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Botão salvar */}
      <div className="flex justify-end pt-2 border-t border-border/40">
        <Button
          size="sm"
          onClick={() => onSave(local)}
          disabled={saving}
        >
          {saving ? 'Salvando…' : 'Salvar Não Perturbe'}
        </Button>
      </div>
    </div>
  );
}
